import { memo, useState } from 'react';
import { Info, Copy, ChevronDown, ChevronUp } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { isInAppBrowser, getInAppBrowserName, isIOSDevice } from '@/utils/platform';
import { APP_CONFIG } from '@/lib/constants';

interface InAppBrowserWarningProps {
  className?: string;
}

export const InAppBrowserWarning = memo(function InAppBrowserWarning({ className = '' }: InAppBrowserWarningProps) {
  const isInApp = isInAppBrowser();
  const browserName = getInAppBrowserName();
  // 25/09: iPhone volta para o Safari (instalação percorrida no simulador, iOS 27;
  // decisão dele revogou o P-216b de 18/08). Android segue no Chrome.
  const targetBrowser = isIOSDevice() ? 'Safari' : 'Chrome';
  const [expanded, setExpanded] = useState(false);

  if (!isInApp) return null;

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(APP_CONFIG.APP_URL);
      toast.success('Link copied!', {
        description: `Now open ${targetBrowser} and paste it there`,
        duration: 5000,
      });
    } catch {
      const textArea = document.createElement('textarea');
      textArea.value = APP_CONFIG.APP_URL;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      toast.success('Link copied!', {
        description: `Now open ${targetBrowser} and paste it there`,
        duration: 5000,
      });
    }
  };

  const appName = browserName?.includes('Instagram') ? 'Instagram'
    : browserName?.includes('Facebook') ? 'Facebook'
      : browserName?.includes('TikTok') ? 'TikTok'
        : browserName?.includes('Messenger') ? 'Messenger'
          : 'this app';

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`p-3.5 rounded-xl bg-amber-50 dark:bg-amber-500/10 ring-1 ring-amber-200 dark:ring-amber-500/20 ${className}`}
    >
      <div className="flex items-center gap-2.5">
        <div className="w-8 h-8 rounded-full bg-amber-100 dark:bg-amber-500/20 flex items-center justify-center flex-shrink-0">
          <Info className="w-4 h-4 text-amber-600 dark:text-amber-400" />
        </div>

        <div className="flex-1 min-w-0">
          <p className="text-amber-800 dark:text-amber-300 text-sm leading-snug">
            For the best experience, open this in <strong>{targetBrowser}</strong>.
          </p>
        </div>

        <button
          onClick={() => setExpanded(!expanded)}
          className="p-1.5 rounded-lg text-amber-500 hover:bg-amber-100 dark:hover:bg-amber-500/20 transition-colors flex-shrink-0"
        >
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
      </div>

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            <div className="mt-3 pt-3 border-t border-amber-200 dark:border-amber-500/20">
              <p className="text-amber-700 dark:text-amber-400/80 text-xs leading-relaxed mb-3">
                You're viewing this from <strong>{appName}</strong>. You can continue here, but {targetBrowser} gives you a smoother experience.{targetBrowser === 'Chrome' ? " It's free to download if you don't have it." : ''}
              </p>

              <button
                onClick={handleCopyLink}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-600 text-white font-semibold text-sm transition-colors"
              >
                <Copy className="w-3.5 h-3.5" />
                Copy Link to Open in {targetBrowser}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
});
